import React from 'react';
import { Interpreter } from 'xstate';
import { useService } from '@xstate/react';
import _startCase from 'lodash/startCase';
import { PMCConfig } from '@kyruus/types';
import { FormattedMessage } from '@kyruus/intl';

import { Log, SortOption } from 'Src/types';
import {
  LocationContext,
  LocationEvent,
  LocationStateSchema,
  EVENTS
} from 'Src/machines/location-v9/types';
import {
  getDefaultCustomerSort,
  getSortOptions
} from 'Src/utils/search-common';
import { messages } from 'Src/shared/sort-menu';
import { SortButtons, SortButton } from './styles';
import { LOGGING_EVENTS } from '../constants';

interface SortMenuProps {
  config: PMCConfig;
  service: Interpreter<LocationContext, LocationStateSchema, LocationEvent>;
  log: Log;
}

export const SortMenu = ({ config, service, log }: SortMenuProps) => {
  const [state, send] = useService(service);

  const defaultSort = getDefaultCustomerSort(config);

  const currentSort = state.context.currentQuery.sort || defaultSort;

  const sortOptionsWithoutDistance: SortOption[] = getSortOptions(
    config,
    state.context.currentQuery
  ).filter((item: SortOption) => item.descriptor_id !== 'distance');

  const handleClick = (sort: string) => {
    log(LOGGING_EVENTS.CLICK_SORT_PROVIDERS, { sort });
    send({
      type: EVENTS.newProvidersSearch,
      data: {
        currentQuery: {
          ...state.context.currentQuery,
          sort
        }
      }
    });
  };

  return (
    <SortButtons data-testid="sort-menu">
      {sortOptionsWithoutDistance.map(({ descriptor_id, value }) => {
        const descriptor = messages[descriptor_id as keyof typeof messages] || {
          id: `sortdropdown.${descriptor_id}`,
          defaultMessage: _startCase(descriptor_id),
          description: `Option for ${descriptor_id} sort`
        };

        return (
          <FormattedMessage key={descriptor_id} {...descriptor}>
            {(text) => (
              <SortButton
                onClick={() => handleClick(value)}
                mode="flat"
                disabled={currentSort === value}
                data-testid={`sort-button-${descriptor_id}`}
              >
                {text}
              </SortButton>
            )}
          </FormattedMessage>
        );
      })}
    </SortButtons>
  );
};

SortMenu.displayName = 'SortMenu';
